import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@mui/material';
import { ordersAPI, settingsAPI } from '../../services/apiService';
import CuatomerReceipt from '../CuatomerReceipt/CuatomerReceipt';
import './OrderWorkflow.css';

const ORDER_STATUSES = ['pending', 'preparing', 'served' /* 'paid' */];
const ORDER_WORKFLOW = {
    pending: {
        primaryAction: {
            label: 'approve',
            fallback: 'Approve',
            targetStatus: 'preparing',
            color: 'success',
        },
        secondaryAction: {
            label: 'reject',
            fallback: 'Reject',
            targetStatus: 'cancelled',
            color: 'error',
        },
    },

    preparing: {
        primaryAction: {
            label: 'mark_served',
            fallback: 'Served',
            targetStatus: 'served',
            color: 'success',
        },
        secondaryAction: {
            label: 'return_to_pending',
            fallback: 'Return to Pending',
            targetStatus: 'pending',
            color: 'warning',
        },
    },

    served: {
        primaryAction: {
            label: 'mark_paid',
            fallback: 'Paid',
            targetStatus: 'paid',
            color: 'success',
        },
        secondaryAction: {
            label: 'cancel_order',
            fallback: 'Cancel',
            targetStatus: 'cancelled',
            color: 'error',
        },
    },

    paid: {
        primaryAction: {
            label: 'undo_payment',
            fallback: 'Return to Served',
            targetStatus: 'served',
            color: 'warning',
        },
        secondaryAction: null,
    },
};

function OrderWorkflow({ orders = [], setOrders, isStaff }) {
    const { t, i18n } = useTranslation();

    const [activeStatus, setActiveStatus] = useState('pending');
    const [currentIndex, setCurrentIndex] = useState(0);
    const [processingId, setProcessingId] = useState(null);
    const [taxPercent, setTaxPercent] = useState(0);
    const [addres, setAddres] = useState('');
    const [brandName, setBrandName] = useState('');
    const [logo, setLogo] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');

    const [openReceiptDialog, setOpenReceiptDialog] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const customerReceiptRef = useRef(null);

    const statusCounts = useMemo(() => {
        const counts = {
            pending: 0,
            preparing: 0,
            served: 0,
            paid: 0,
        };

        orders.forEach((order) => {
            const status = String(order?.status || '').toLowerCase();

            if (Object.prototype.hasOwnProperty.call(counts, status)) {
                counts[status] += 1;
            }
        });

        return counts;
    }, [orders]);

    const currentStatusOrders = useMemo(
        () => orders.filter((order) => String(order?.status || '').toLowerCase() === activeStatus),
        [orders, activeStatus]
    );

    const currentOrder = currentStatusOrders[currentIndex];

    useEffect(() => {
        if (currentStatusOrders.length === 0) {
            setCurrentIndex(0);
            return;
        }

        if (currentIndex >= currentStatusOrders.length) {
            setCurrentIndex(currentStatusOrders.length - 1);
        }
    }, [currentStatusOrders.length, currentIndex]);
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const settingsData = await settingsAPI.getSettings();
                setTaxPercent(Number(settingsData?.tax || 0));
                setBrandName(settingsData?.brand_name || '');
                setLogo(settingsData?.logo || '');
                setPhoneNumber(settingsData?.phone_number || '');
                setAddres(settingsData?.address || '');
            } catch (error) {
                console.error('Failed to load receipt settings:', error);
            }
        };
        fetchSettings();
    }, []);

    const handleTabChange = (status) => {
        setActiveStatus(status);
        setCurrentIndex(0);
    };

    const handlePrevious = () => {
        if (currentStatusOrders.length <= 1) return;

        setCurrentIndex((previous) =>
            previous === 0 ? currentStatusOrders.length - 1 : previous - 1
        );
    };

    const handleNext = () => {
        if (currentStatusOrders.length <= 1) return;

        setCurrentIndex((previous) =>
            previous === currentStatusOrders.length - 1 ? 0 : previous + 1
        );
    };

    const handleStatusChange = async (targetStatus) => {
        if (!currentOrder?.id || !targetStatus) return;

        try {
            setProcessingId(currentOrder.id);

            await ordersAPI.update(currentOrder.id, {
                status: targetStatus,
            });

            setOrders((previousOrders) =>
                previousOrders.map((order) =>
                    order.id === currentOrder.id
                        ? {
                              ...order,
                              status: targetStatus,
                          }
                        : order
                )
            );
        } catch (error) {
            console.error('Failed to update order status:', error);
        } finally {
            setProcessingId(null);
        }
    };

    const handleOpenReceiptDialog = (order) => {
        setSelectedOrder(order);
        setOpenReceiptDialog(true);
    };

    const handleCloseReceiptDialog = () => {
        setSelectedOrder(null);
        setOpenReceiptDialog(false);
    };

    const formatPrice = (value) =>
        Number(value || 0).toLocaleString(i18n.language === 'fa' ? 'fa-IR' : 'en-US');

    const getOrderFinalPrice = (order) => Number(order?.total_price || 0);

    const getOrderDiscount = (order) => Number(order?.discount || 0);

    const getCustomerName = (order) => {
        if (!order) return '-';

        if (order.customer === null || order.customer === undefined) {
            return t('walk_in_customer') || 'Walk-in Customer';
        }

        if (order.customer_name) {
            return order.customer_name;
        }

        if (typeof order.customer === 'object') {
            return order.customer.full_name || order.customer.name || t('walk_in_customer') || '-';
        }

        if (typeof order.customer === 'string' && order.customer.trim()) {
            return order.customer;
        }

        return t('walk_in_customer') || 'Walk-in Customer';
    };

    const getTableDisplayName = (order) => {
        if (!order?.table) {
            return t('take_away_order') || 'Take away order';
        }

        return order.table.table_number || order.table;
    };

    const getStatusClassName = (status) => {
        const normalizedStatus = String(status || '').toLowerCase();

        switch (normalizedStatus) {
            case 'pending':
                return 'status_badge status_pending';

            case 'preparing':
                return 'status_badge status_preparing';

            case 'served':
                return 'status_badge status_served';

            case 'paid':
                return 'status_badge status_paid';

            case 'cancelled':
                return 'status_badge status_cancelled';

            default:
                return 'status_badge status_unknown';
        }
    };

    const getTranslatedStatus = (status) => {
        const normalizedStatus = String(status || '').toLowerCase();

        if (!normalizedStatus) return '-';

        const translated = t(`order_status.${normalizedStatus}`);

        return translated === `order_status.${normalizedStatus}` ? status : translated;
    };

    const currentWorkflow = ORDER_WORKFLOW[activeStatus];

    return (
        <>
            <div className="order_workflow_section">
                <div className="order_workflow_header">
                    {t('order_workflow') || 'Order Workflow'}
                </div>
                <div className="order_workflow_tabs">
                    {ORDER_STATUSES.map((status) => (
                        <button
                            type="button"
                            key={status}
                            className={`order_workflow_tab ${
                                activeStatus === status ? 'order_workflow_tab_active' : ''
                            }`}
                            onClick={() => handleTabChange(status)}
                        >
                            <span className="order_workflow_tab_label">
                                {getTranslatedStatus(status)}
                            </span>

                            {statusCounts[status] > 0 && (
                                <span className="order_workflow_count">
                                    {formatPrice(statusCounts[status])}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
                {currentOrder ? (
                    <>
                        <TableContainer component={Paper} className="order_workflow_table">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="center">
                                            {t('customer_name') || 'Customer Name'}
                                        </TableCell>

                                        <TableCell align="center">
                                            {t('table_number') || 'Table Number'}
                                        </TableCell>

                                        <TableCell align="center">
                                            {t('status') || 'Status'}
                                        </TableCell>

                                        <TableCell align="center">
                                            {t('created_date') || 'Created Date'}
                                        </TableCell>

                                        <TableCell align="center">
                                            {t('discount') || 'Discount'}
                                        </TableCell>

                                        <TableCell align="center">
                                            {t('total_price') || 'Total Price'}
                                        </TableCell>

                                        <TableCell align="center">
                                            {t('items') || 'Items'}
                                        </TableCell>

                                        <TableCell align="center">
                                            {t('actions') || 'Actions'}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    <TableRow>
                                        <TableCell
                                            align="center"
                                            data-label={t('customer_name') || 'Customer Name'}
                                        >
                                            {getCustomerName(currentOrder)}
                                        </TableCell>

                                        <TableCell
                                            align="center"
                                            data-label={t('table_number') || 'Table Number'}
                                        >
                                            {getTableDisplayName(currentOrder)}
                                        </TableCell>

                                        <TableCell
                                            align="center"
                                            data-label={t('status') || 'Status'}
                                        >
                                            <span
                                                className={getStatusClassName(currentOrder.status)}
                                            >
                                                {getTranslatedStatus(currentOrder.status)}
                                            </span>
                                        </TableCell>

                                        <TableCell
                                            className="order_workflow_date"
                                            align="center"
                                            data-label={t('created_date') || 'Created Date'}
                                        >
                                            {currentOrder.created_at
                                                ? new Date(currentOrder.created_at).toLocaleString(
                                                      i18n.language === 'fa' ? 'fa-IR' : 'en-US',
                                                      {
                                                          day: '2-digit',
                                                          hour: '2-digit',
                                                          minute: '2-digit',
                                                          year: 'numeric',
                                                          month: '2-digit',
                                                          hour12: false,
                                                      }
                                                  )
                                                : '-'}
                                        </TableCell>

                                        <TableCell
                                            align="center"
                                            data-label={t('discount') || 'Discount'}
                                        >
                                            {formatPrice(getOrderDiscount(currentOrder))}
                                        </TableCell>

                                        <TableCell
                                            align="center"
                                            data-label={t('total_price') || 'Total Price'}
                                        >
                                            {formatPrice(getOrderFinalPrice(currentOrder))}
                                        </TableCell>

                                        <TableCell
                                            align="center"
                                            data-label={t('items') || 'Items'}
                                        >
                                            <Button
                                                className="order_workflow_view_items"
                                                onClick={() =>
                                                    handleOpenReceiptDialog(currentOrder)
                                                }
                                            >
                                                {t('view_items') || 'View Items'}
                                            </Button>
                                        </TableCell>

                                        <TableCell
                                            align="center"
                                            data-label={t('actions') || 'Actions'}
                                        >
                                            {isStaff && currentWorkflow?.primaryAction && (
                                                <Button
                                                    variant="outlined"
                                                    color={currentWorkflow.primaryAction.color}
                                                    className="order_workflow_primary_action"
                                                    onClick={() =>
                                                        handleStatusChange(
                                                            currentWorkflow.primaryAction
                                                                .targetStatus
                                                        )
                                                    }
                                                    disabled={processingId === currentOrder.id}
                                                >
                                                    {processingId === currentOrder.id
                                                        ? t('processing') || 'Processing...'
                                                        : t(currentWorkflow.primaryAction.label) ||
                                                          currentWorkflow.primaryAction.fallback}
                                                </Button>
                                            )}

                                            {isStaff && currentWorkflow?.secondaryAction && (
                                                <Button
                                                    variant="outlined"
                                                    color={currentWorkflow.secondaryAction.color}
                                                    onClick={() =>
                                                        handleStatusChange(
                                                            currentWorkflow.secondaryAction
                                                                .targetStatus
                                                        )
                                                    }
                                                    disabled={processingId === currentOrder.id}
                                                >
                                                    {processingId === currentOrder.id
                                                        ? t('processing') || 'Processing...'
                                                        : t(
                                                              currentWorkflow.secondaryAction.label
                                                          ) ||
                                                          currentWorkflow.secondaryAction.fallback}
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {currentStatusOrders.length > 1 && (
                            <div className="order_workflow_navigation">
                                <Button
                                    variant="outlined"
                                    className="order_workflow_nav_btn"
                                    onClick={handlePrevious}
                                >
                                    {t('previousOrder') || 'Previous'}
                                </Button>

                                <span className="order_workflow_counter">
                                    {t('order') || 'Order'} {formatPrice(currentIndex + 1)}{' '}
                                    {t('of') || 'of'} {formatPrice(currentStatusOrders.length)}
                                </span>

                                <Button
                                    variant="outlined"
                                    className="order_workflow_nav_btn"
                                    onClick={handleNext}
                                >
                                    {t('nextOrder') || 'Next'}
                                </Button>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="order_workflow_empty">
                        {t('no_orders_in_this_status') || `No ${activeStatus} orders`}
                    </div>
                )}
            </div>

            <Dialog
                open={openReceiptDialog}
                onClose={handleCloseReceiptDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogContent>
                    <CuatomerReceipt
                        ref={customerReceiptRef}
                        order={selectedOrder}
                        orders={orders}
                        brandName={brandName}
                        taxPercent={taxPercent}
                        logo={logo}
                        phoneNumber={phoneNumber}
                        addres={addres}
                    />
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => customerReceiptRef.current?.print()} color="primary">
                        {t('print_customer_receipt') || 'Print Receipt'}
                    </Button>
                    <Button onClick={handleCloseReceiptDialog}>{t('close') || 'Close'}</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default OrderWorkflow;
